import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon, UserPlus, Clock, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

interface Manager {
  id: string;
  name: string;
  email: string;
  role: "admin" | "manager";
}

interface EditPackageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageData: {
    id: string;
    managerId?: string;
    pickupDate?: Date;
    pickupTimeSlot?: string;
  } | null;
  managers: Manager[];
  onSave: (data: {
    packageId: string;
    managerId: string | null;
    pickupDate: Date | null;
    pickupTimeSlot: string | null;
  }) => Promise<void>;
  loading?: boolean;
}

const TIME_SLOTS = [
  { value: "8am-10am", label: "8:00 AM - 10:00 AM" },
  { value: "10am-12pm", label: "10:00 AM - 12:00 PM" },
  { value: "12pm-2pm", label: "12:00 PM - 2:00 PM" },
  { value: "2pm-4pm", label: "2:00 PM - 4:00 PM" },
  { value: "4pm-6pm", label: "4:00 PM - 6:00 PM" },
];

export const EditPackageDialog = ({
  open,
  onOpenChange,
  packageData,
  managers,
  onSave,
  loading = false,
}: EditPackageDialogProps) => {
  const [selectedManager, setSelectedManager] = useState<string>("none");
  const [pickupDate, setPickupDate] = useState<Date | undefined>();
  const [pickupTimeSlot, setPickupTimeSlot] = useState<string>("none");

  // Reset form when package changes
  useEffect(() => {
    if (packageData) {
      setSelectedManager(packageData.managerId || "none");
      setPickupDate(packageData.pickupDate);
      setPickupTimeSlot(packageData.pickupTimeSlot || "none");
    }
  }, [packageData]);

  const handleSave = async () => {
    if (!packageData) return;

    await onSave({
      packageId: packageData.id,
      managerId: selectedManager === "none" ? null : selectedManager || null,
      pickupDate: pickupDate || null,
      pickupTimeSlot: pickupTimeSlot === "none" ? null : pickupTimeSlot || null,
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const selectedManagerData = managers.find((m) => m.id === selectedManager);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background">
        <DialogHeader>
          <DialogTitle>Edit Package</DialogTitle>
          <DialogDescription>
            Update the assigned manager and pickup schedule for this package.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Manager Selection */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Assigned Manager
            </Label>
            <Select value={selectedManager} onValueChange={setSelectedManager}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a manager...">
                  {selectedManagerData ? (
                    <div className="flex items-center gap-2">
                      <span>{selectedManagerData.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {selectedManagerData.role === "admin" ? "Admin" : "Manager"}
                      </Badge>
                    </div>
                  ) : (
                    "Select a manager..."
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="none">
                  <span className="text-muted-foreground">No manager assigned</span>
                </SelectItem>
                {managers.map((manager) => (
                  <SelectItem key={manager.id} value={manager.id}>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs bg-primary/10 text-primary">
                          {getInitials(manager.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex items-center gap-2">
                        <span>{manager.name}</span>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs",
                            manager.role === "admin"
                              ? "bg-primary/10 text-primary border-primary/30"
                              : "bg-blue-100 text-blue-800 border-blue-200"
                          )}
                        >
                          {manager.role === "admin" ? "Admin" : "Manager"}
                        </Badge>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Pickup Date */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              Pickup Date
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !pickupDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {pickupDate ? format(pickupDate, "PPP") : "Select a date..."}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-popover" align="start">
                <Calendar
                  mode="single"
                  selected={pickupDate}
                  onSelect={setPickupDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  className="p-3 pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Slot */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pickup Time Slot
            </Label>
            <Select value={pickupTimeSlot} onValueChange={setPickupTimeSlot}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a time slot..." />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="none">
                  <span className="text-muted-foreground">No time slot selected</span>
                </SelectItem>
                {TIME_SLOTS.map((slot) => (
                  <SelectItem key={slot.value} value={slot.value}>
                    {slot.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
